import React, { useCallback, useEffect, useRef, useState } from 'react';
import { X, Camera, Image as ImageIcon, Trash2, Video } from 'lucide-react';
import { compressImage } from './utils/imageUtils';

function CameraModal({ open, onClose, images, onImagesChange }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const cameraInputRef = useRef(null);
  const galleryInputRef = useRef(null);
  const [streamActive, setStreamActive] = useState(false);
  const [cameraReady, setCameraReady] = useState(false);
  const [openingCamera, setOpeningCamera] = useState(false);
  const [processingImage, setProcessingImage] = useState(false);
  const [cameraError, setCameraError] = useState('');
  const [previewUrls, setPreviewUrls] = useState([]);

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }

    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }

    setStreamActive(false);
    setCameraReady(false);
    setOpeningCamera(false);
  }, []);

  useEffect(() => {
    return () => stopCamera();
  }, [stopCamera]);

  useEffect(() => {
    if (!open) {
      stopCamera();
      setCameraError('');
    }
  }, [open, stopCamera]);

  useEffect(() => {
    const urls = images.map((blob) => URL.createObjectURL(blob));
    setPreviewUrls(urls);

    return () => {
      urls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [images]);

  const addImage = useCallback((image) => {
    onImagesChange((currentImages) => [...currentImages, image]);
  }, [onImagesChange]);

  const openLiveCamera = async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      setCameraError('La vista en vivo no esta disponible en este dispositivo. Usa Camara o Galeria.');
      return;
    }

    stopCamera();
    setCameraError('');
    setOpeningCamera(true);

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: {
          facingMode: 'environment',
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
      });

      streamRef.current = stream;
      setStreamActive(true);
    } catch (error) {
      console.error('Error opening live camera:', error);
      setCameraError('No se pudo abrir la vista en vivo. Revisa permisos o usa Camara.');
      setOpeningCamera(false);
    }
  };

  useEffect(() => {
    const video = videoRef.current;
    const stream = streamRef.current;

    if (!streamActive || !video || !stream) return;

    video.srcObject = stream;
    const handleReady = () => setCameraReady(true);
    video.addEventListener('loadedmetadata', handleReady);
    video.addEventListener('canplay', handleReady);

    video.play()
      .then(() => setOpeningCamera(false))
      .catch((error) => {
        console.error('Error playing live camera:', error);
        setCameraError('La camara se abrio, pero el video no pudo iniciar.');
        setOpeningCamera(false);
      });

    return () => {
      video.removeEventListener('loadedmetadata', handleReady);
      video.removeEventListener('canplay', handleReady);
    };
  }, [streamActive]);

  const processSelectedFile = async (file) => {
    if (!file) return;

    setProcessingImage(true);
    try {
      const compressed = await compressImage(file, 0.6, 1000, 1000);
      addImage(compressed);
    } catch (error) {
      console.error('Error compressing image:', error);
      addImage(file);
    } finally {
      setProcessingImage(false);
    }
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files?.[0];
    await processSelectedFile(file);
    event.target.value = '';
  };

  const capturePhoto = async () => {
    const video = videoRef.current;
    if (!streamRef.current || !video || !cameraReady || !video.videoWidth || !video.videoHeight) return;

    setProcessingImage(true);
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const context = canvas.getContext('2d');
    if (!context) {
      setProcessingImage(false);
      setCameraError('No se pudo preparar la imagen en este dispositivo.');
      return;
    }

    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    canvas.toBlob(async (blob) => {
      if (!blob) {
        setProcessingImage(false);
        return;
      }

      try {
        const compressed = await compressImage(blob, 0.6, 1000, 1000);
        addImage(compressed);
      } catch (error) {
        console.error('Error compressing captured image:', error);
        addImage(blob);
      } finally {
        setProcessingImage(false);
      }
    }, 'image/jpeg', 0.85);
  };

  const handleRemoveImage = (index) => {
    onImagesChange((currentImages) => currentImages.filter((_, imageIndex) => imageIndex !== index));
  };

  const handleClose = () => {
    stopCamera();
    onClose();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center p-4" style={{ zIndex: 2000 }}>
      <div className="absolute inset-0 bg-black/50" onClick={handleClose} />
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto relative">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-900">Imagenes</h2>
          <button
            onClick={handleClose}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            aria-label="Cerrar"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {streamActive && (
            <div className="relative bg-black rounded-lg overflow-hidden aspect-video">
              <video
                ref={videoRef}
                muted
                playsInline
                className="w-full h-full object-cover"
              />
              {!cameraReady && (
                <div className="absolute inset-0 flex items-center justify-center text-white text-sm font-medium bg-black/40">
                  Iniciando camara...
                </div>
              )}
            </div>
          )}

          {cameraError && (
            <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
              {cameraError}
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => cameraInputRef.current?.click()}
              disabled={processingImage}
              className="bg-brand-gold text-white py-3 rounded-lg font-semibold hover:bg-brand-gold/90 transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
            >
              <Camera className="w-5 h-5" />
              Camara
            </button>

            <button
              onClick={() => galleryInputRef.current?.click()}
              disabled={processingImage}
              className="bg-gray-100 text-gray-900 py-3 rounded-lg font-semibold hover:bg-gray-200 transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
            >
              <ImageIcon className="w-5 h-5" />
              Galeria
            </button>

            <input
              ref={cameraInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              onChange={handleFileSelect}
              className="hidden"
            />
            <input
              ref={galleryInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              className="hidden"
            />
          </div>

          <div className="flex gap-3">
            {!streamActive ? (
              <button
                onClick={openLiveCamera}
                disabled={openingCamera || processingImage}
                className="flex-1 bg-gray-900 text-white py-3 rounded-lg font-semibold hover:bg-gray-800 transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
              >
                <Video className="w-5 h-5" />
                {openingCamera ? 'Abriendo...' : 'Vista en vivo'}
              </button>
            ) : (
              <>
                <button
                  onClick={capturePhoto}
                  disabled={!cameraReady || processingImage}
                  className="flex-1 bg-brand-green text-white py-3 rounded-lg font-semibold hover:bg-brand-green/90 transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
                >
                  <Camera className="w-5 h-5" />
                  {processingImage ? 'Procesando...' : 'Capturar'}
                </button>
                <button
                  onClick={stopCamera}
                  className="px-4 bg-gray-100 text-gray-900 py-3 rounded-lg font-semibold hover:bg-gray-200 transition-colors"
                >
                  Cerrar video
                </button>
              </>
            )}
          </div>

          {images.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-3">
                Imagenes capturadas ({images.length})
              </h3>
              <div className="grid grid-cols-3 gap-3">
                {previewUrls.map((url, index) => (
                  <div key={url} className="relative group">
                    <img
                      src={url}
                      alt={`Imagen ${index + 1}`}
                      className="w-full aspect-square object-cover rounded-lg border-2 border-gray-200"
                    />
                    <button
                      onClick={() => handleRemoveImage(index)}
                      className="absolute top-1 right-1 bg-red-500 text-white p-1.5 rounded-full opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity shadow-lg"
                      aria-label="Eliminar imagen"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <button
            onClick={handleClose}
            className="w-full bg-brand-gold text-white py-3 rounded-lg font-semibold hover:bg-brand-gold/90 transition-colors"
          >
            Listo
          </button>
        </div>
      </div>
    </div>
  );
}

export default CameraModal;
